import { MapLayerCategory } from '../../../utils/layer-registry';
import { EMPTY_COLLECTION, MEASURE_SOURCES, measureLayers, MeasureSourceId } from './layers';

export type MeasureCollections = Partial<Record<MeasureSourceId, any>>;

/**
 * Owns the measure tool's sources and layers on one map: adds them on top of
 * everything else, swaps their data, and puts them back after a base-map style
 * change (which wipes every custom source and layer).
 */
export class MeasureMapLayers {
  private data: Record<string, any> = {};
  private bandFillMinZoom = 0;
  private readonly onStyleLoad = () => this.ensure();

  constructor(private readonly map: any, private readonly registry: any = (window as any).layerRegistry) {
    MEASURE_SOURCES.forEach((id) => { this.data[id] = EMPTY_COLLECTION; });
  }

  attach(): void {
    this.ensure();
    this.map.on('style.load', this.onStyleLoad);
  }

  update(collections: MeasureCollections, bandFillMinZoom?: number): void {
    Object.entries(collections).forEach(([id, collection]) => {
      this.data[id] = collection || EMPTY_COLLECTION;
      this.map.getSource(id)?.setData(this.data[id]);
    });
    if (bandFillMinZoom !== undefined && bandFillMinZoom !== this.bandFillMinZoom) {
      this.bandFillMinZoom = bandFillMinZoom;
      if (this.map.getLayer('measure-band-fill')) this.map.setLayerZoomRange('measure-band-fill', bandFillMinZoom, 24);
      if (this.map.getLayer('measure-band-line')) this.map.setLayerZoomRange('measure-band-line', 0, Math.max(bandFillMinZoom, 0.01));
    }
  }

  detach(): void {
    this.map.off('style.load', this.onStyleLoad);
    [...measureLayers()].reverse().forEach(({ id }) => {
      if (!this.map.getLayer(id)) return;
      if (this.registry?.removeLayer) this.registry.removeLayer(id, false);
      else this.map.removeLayer(id);
    });
    MEASURE_SOURCES.forEach((id) => {
      if (this.map.getSource(id)) this.map.removeSource(id);
    });
  }

  private ensure(): void {
    MEASURE_SOURCES.forEach((id) => {
      if (!this.map.getSource(id)) this.map.addSource(id, { type: 'geojson', data: this.data[id] });
    });
    measureLayers(this.bandFillMinZoom).forEach((layer) => {
      if (this.map.getLayer(layer.id)) return;
      if (this.registry?.addLayer) this.registry.addLayer(layer, MapLayerCategory.Top);
      else this.map.addLayer(layer);
    });
  }
}
